import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { VanStockTemplate } from '@/components/VanStockTemplate';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Truck, Package, Plus, Trash2, Search, RefreshCw, Save,
  CheckCircle2, XCircle, AlertTriangle, Filter, Info, Loader2
} from 'lucide-react';

export default function VanPermanentStock() {
  const { user } = useContext(AuthContext);
  
  // State
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [permanentItems, setPermanentItems] = useState([]);
  const [usesTemplate, setUsesTemplate] = useState(false);
  const [warehouseProducts, setWarehouseProducts] = useState([]); // Products with stock in selected warehouse
  const [searchQuery, setSearchQuery] = useState('');
  
  // Add items modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [savingItems, setSavingItems] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Load warehouses on mount
  useEffect(() => {
    loadWarehouses();
  }, []);
  
  // Load permanent items and warehouse products when warehouse changes
  useEffect(() => {
    if (selectedWarehouse) {
      loadPermanentItems(selectedWarehouse);
      loadWarehouseProducts(selectedWarehouse);
    }
  }, [selectedWarehouse]);
  
  const loadWarehouses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/van-stock/warehouses/technician`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWarehouses(res.data.warehouses || []);
      
      // Select first warehouse if available
      if (res.data.warehouses?.length > 0 && !selectedWarehouse) {
        setSelectedWarehouse(res.data.warehouses[0].code);
      }
    } catch (err) {
      console.error('Failed to load warehouses:', err);
      toast.error('Erro ao carregar armazéns');
    } finally {
      setLoading(false);
    }
  };
  
  const loadPermanentItems = async (warehouseCode) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/van-stock/permanent/${warehouseCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermanentItems(res.data.items || []);
      setUsesTemplate(!!res.data.uses_template);
    } catch (err) {
      console.error('Failed to load permanent items:', err);
      toast.error('Erro ao carregar stock permanente');
    }
  };
  
  const loadWarehouseProducts = async (warehouseCode, search) => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/van-stock/products-with-stock/${warehouseCode}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: search && search.length >= 2 ? { search } : {}
      });
      setWarehouseProducts(res.data.products || []);
    } catch (err) {
      console.error('Failed to load warehouse products:', err);
      setWarehouseProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };
  
  const handleAddProducts = async () => {
    if (!selectedWarehouse || selectedProducts.length === 0) return;
    
    setSavingItems(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/van-stock/permanent/add`, {
        warehouse_code: selectedWarehouse,
        items: selectedProducts.map(sku => ({ sku, target_quantity: null, notes: null }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${selectedProducts.length} artigo(s) adicionado(s) ao stock permanente`);
      setShowAddModal(false);
      setSelectedProducts([]);
      setProductSearchQuery('');
      loadPermanentItems(selectedWarehouse);
      loadWarehouses(); // Refresh counts
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar artigos');
    } finally {
      setSavingItems(false);
    }
  };
  
  const handleRemoveItem = async (sku) => {
    if (!selectedWarehouse) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/van-stock/permanent/remove`, {
        warehouse_code: selectedWarehouse,
        skus: [sku]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Artigo removido do stock permanente');
      loadPermanentItems(selectedWarehouse);
      loadWarehouses(); // Refresh counts
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover artigo');
    }
  };
  
  const handleRemoveSelected = async (skusToRemove) => {
    if (!selectedWarehouse || skusToRemove.length === 0) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/van-stock/permanent/remove`, {
        warehouse_code: selectedWarehouse,
        skus: skusToRemove
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${skusToRemove.length} artigo(s) removido(s) do stock permanente`);
      loadPermanentItems(selectedWarehouse);
      loadWarehouses(); // Refresh counts
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover artigos');
    }
  };
  
  // Debounced update of target quantity
  const handleUpdateTargetQuantity = async (sku, newQuantity) => {
    if (!selectedWarehouse) return;
    
    // Update local state immediately for responsiveness
    setPermanentItems(prev => prev.map(item => 
      item.sku === sku ? { ...item, target_quantity: newQuantity } : item
    ));
    
    // Debounce the API call (save after 500ms of no changes)
    clearTimeout(window._targetQtyTimeout);
    window._targetQtyTimeout = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/van-stock/permanent/add`, {
          warehouse_code: selectedWarehouse,
          items: [{ sku, target_quantity: newQuantity, notes: null }]
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Silent success - no toast for quantity updates
      } catch (err) {
        toast.error('Erro ao atualizar quantidade alvo');
        // Reload to get actual value
        loadPermanentItems(selectedWarehouse);
      }
    }, 500);
  };
  
  // Filter permanent items by search
  const filteredItems = permanentItems.filter(item => 
    !searchQuery || 
    item.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Filter products for add modal (exclude already permanent, show only with stock in warehouse)
  const permanentSkus = new Set(permanentItems.map(i => i.sku));
  const availableProducts = warehouseProducts.filter(p => 
    !permanentSkus.has(p.sku) &&
    (!productSearchQuery || 
      p.sku?.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
      p.name?.toLowerCase().includes(productSearchQuery.toLowerCase()))
  );
  
  const selectedWarehouseData = warehouses.find(w => w.code === selectedWarehouse);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Truck className="w-6 h-6 text-blue-600" />
            Stock Permanente - Carrinhas
          </h1>
          <p className="text-slate-500 mt-1">
            Defina quais artigos são considerados stock permanente em cada carrinha.
            Apenas stock permanente será incluído no processo de reposição.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            loadWarehouses();
            if (selectedWarehouse) loadPermanentItems(selectedWarehouse);
          }}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>
      
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Como funciona:</p>
          <ul className="mt-1 space-y-1 list-disc list-inside text-blue-700">
            <li>Artigos marcados como <strong>permanentes</strong> serão automaticamente repostos quando consumidos</li>
            <li>Artigos <strong>não permanentes</strong> (transferidos para serviços específicos) não entram na reposição</li>
            <li>Pode adicionar ou remover artigos a qualquer momento</li>
          </ul>
        </div>
      </div>
      
      {/* Warehouse Selector */}
      <VanStockTemplate warehouses={warehouses} onChanged={() => { loadWarehouses(); if (selectedWarehouse) loadPermanentItems(selectedWarehouse); }} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Selecionar Carrinha</CardTitle>
        </CardHeader>
        <CardContent>
          {warehouses.length === 0 ? (
            <div className="text-center py-8">
              <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-600 font-medium">Nenhuma carrinha encontrada</p>
              <p className="text-slate-500 text-sm mt-1">
                Não foram encontrados armazéns de técnicos configurados no sistema.
                Verifique se existem armazéns com códigos de carrinha (ex: T01, T02) na configuração SAGE.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="col-span-2">
                <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                  <SelectTrigger data-testid="van-warehouse-select">
                    <SelectValue placeholder="Selecione uma carrinha..." />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(wh => (
                      <SelectItem key={wh.code} value={wh.code}>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-slate-400" />
                          <span>{wh.name || wh.code}</span>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {wh.permanent_stock_count} perm.
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedWarehouse && (
                <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="van-add-items-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Artigos
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Permanent Stock Table */}
      {selectedWarehouse && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-lg">
                  Stock Permanente: {selectedWarehouseData?.name || selectedWarehouse}
                </CardTitle>
                <CardDescription>
                  {filteredItems.length} artigo(s) configurado(s){usesTemplate ? ' · usa o stock padrão Chaviarte' : ''}
                </CardDescription>
                {usesTemplate && (
                  <Button size="sm" variant="outline" className="mt-2" data-testid="van-reset-to-template"
                    onClick={async () => {
                      if (!window.confirm('Descartar os ajustes desta carrinha e voltar ao stock padrão?')) return;
                      try {
                        await axios.post(`${API}/van-stock/permanent/${selectedWarehouse}/reset`);
                        toast.success('Carrinha reposta ao stock padrão');
                        loadPermanentItems(selectedWarehouse);
                        loadWarehouses();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Erro ao repor stock padrão');
                      }
                    }}>Repor stock padrão</Button>
                )}
              </div>
              
              {/* Search */}
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar SKU ou nome..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredItems.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Sem artigos permanentes configurados</p>
                <p className="text-sm mt-1">
                  Clique em "Adicionar Artigos" para definir o stock permanente desta carrinha.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Caixa</TableHead>
                    <TableHead className="text-center w-[120px]">Qty Alvo</TableHead>
                    <TableHead className="text-right">Origem</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map(item => (
                    <TableRow key={item.sku}>
                      <TableCell className="font-mono font-medium">{item.sku}</TableCell>
                      <TableCell>{item.product_name || '—'}</TableCell>
                      <TableCell className="text-xs">{item.box || '—'}</TableCell>
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          min="0"
                          className="w-20 text-center h-8"
                          value={item.target_quantity ?? ''}
                          onChange={(e) => handleUpdateTargetQuantity(item.sku, parseInt(e.target.value) || 0)}
                          placeholder="0"
                          data-testid={`van-item-qty-${item.sku}`}
                        />
                      </TableCell>
                      <TableCell className="text-right" data-testid={`van-item-source-${item.sku}`}>
                        {item.source === 'template' && <Badge className="bg-blue-100 text-blue-800">Padrão</Badge>}
                        {item.source === 'template_adjusted' && <Badge className="bg-indigo-100 text-indigo-800">Padrão ajustado</Badge>}
                        {item.source === 'van' && <Badge className="bg-emerald-100 text-emerald-800">Só desta carrinha</Badge>}
                        {!item.source && <span className="text-sm text-slate-500">{item.added_by_name || '—'}</span>}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`van-item-remove-${item.sku}`}
                          onClick={() => window.confirm(`Remover ${item.sku} desta carrinha?`) && handleRemoveItem(item.sku)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Add Products Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              Adicionar Artigos ao Stock Permanente
            </DialogTitle>
            <DialogDescription>
              Selecione os artigos com stock em {selectedWarehouseData?.name || selectedWarehouse} que devem fazer parte do stock permanente.
              Pesquise (2 ou mais caracteres) para procurar em todo o catálogo.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Pesquisar produtos por SKU ou nome..."
                value={productSearchQuery}
                onChange={(e) => {
                  setProductSearchQuery(e.target.value);
                  clearTimeout(window.__vanSearchT);
                  const v = e.target.value;
                  window.__vanSearchT = setTimeout(() => selectedWarehouse && loadWarehouseProducts(selectedWarehouse, v), 350);
                }}
                className="pl-10"
              />
            </div>
            
            {/* Selected count */}
            {selectedProducts.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 flex items-center justify-between">
                <span className="text-sm text-blue-800">
                  {selectedProducts.length} artigo(s) selecionado(s)
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedProducts([])}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Limpar
                </Button>
              </div>
            )}
            
            {/* Products list */}
            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              {loadingProducts ? (
                <div className="text-center py-8 text-slate-500">
                  <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-slate-400" />
                  <p className="text-sm">A carregar produtos...</p>
                </div>
              ) : availableProducts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">
                    {productSearchQuery 
                      ? 'Nenhum produto encontrado' 
                      : warehouseProducts.length === 0
                        ? 'Sem produtos com stock nesta carrinha'
                        : 'Todos os produtos com stock já são permanentes'}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]"></TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead className="text-center w-[80px]">Stock</TableHead>
                      <TableHead>Categoria</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availableProducts.slice(0, 100).map(product => (
                      <TableRow 
                        key={product.sku}
                        className={selectedProducts.includes(product.sku) ? 'bg-blue-50' : 'cursor-pointer hover:bg-slate-50'}
                        onClick={() => {
                          if (selectedProducts.includes(product.sku)) {
                            setSelectedProducts(prev => prev.filter(s => s !== product.sku));
                          } else {
                            setSelectedProducts(prev => [...prev, product.sku]);
                          }
                        }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedProducts.includes(product.sku)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedProducts(prev => [...prev, product.sku]);
                              } else {
                                setSelectedProducts(prev => prev.filter(s => s !== product.sku));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell className="text-center font-medium">{product.current_stock || 0}</TableCell>
                        <TableCell>
                          {product.category && (
                            <Badge variant="outline" className="text-xs">{product.category}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {availableProducts.length > 100 && (
                <div className="text-center py-2 text-xs text-slate-500 border-t">
                  Mostrando 100 de {availableProducts.length} resultados. Refine a pesquisa.
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddProducts}
              disabled={selectedProducts.length === 0 || savingItems}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingItems ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A guardar...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
